import PropTypes from "prop-types";
import React from "react";

import { SMALL, useScreenSize } from "@swa-ui/browser";
import { ButtonGroup } from "@swa-ui/core";
import { DOLLARS, DOLLARS_PLUS_POINTS, POINTS } from "@swa-ui/currency";
import { SelectFormField } from "@swa-ui/form";
import i18n from "@swa-ui/locale";

export const CURRENCY_SELECTOR_FIELD_SCHEMA_NAME = "currency";

/**
 * Currency selection component that provides interface for choosing between dollars, points, or dollars plus points.
 * Renders as a dropdown on small screens and button group on larger screens.
 */
export const CurrencySelector = (props) => {
  const { currencyType, onCurrencyChange, showDollarsPlusPoints = true } = props;
  const { screenSize } = useScreenSize();

  return screenSize === SMALL ? (
    <SelectFormField {...getCurrencyFormFieldProps()} />
  ) : (
    <ButtonGroup {...getCurrencyButtonGroupProps()} />
  );

  function getCurrencyFormFieldProps() {
    const currencyOptions = getCurrencyOptions();

    return {
      componentProps: {
        "aria-label": i18n("CurrencySelector__ARIA_LABEL"),
        defaultValue: currencyType,
        list: currencyOptions,
        onChange: handleCurrencySelectChange,
        size: "small",
      },
      label: i18n("CurrencySelector__CURRENCY"),
      name: CURRENCY_SELECTOR_FIELD_SCHEMA_NAME,
    };
  }

  function getCurrencyButtonGroupProps() {
    const currencyOptions = getCurrencyOptions();
    const buttonList = currencyOptions.map(({ label, ariaLabel }) => ({
      label,
      props: { "aria-label": ariaLabel },
    }));
    const selectedIndex = currencyOptions.findIndex((option) => option.value === currencyType);

    return {
      "aria-label-next": i18n("Accessibility__BUTTON_GROUP_NEXT"),
      "aria-label-previous": i18n("Accessibility__BUTTON_GROUP_PREVIOUS"),
      buttonList,
      onClick: handleCurrencyButtonClick,
      selectedIndex,
      size: "small",
      spaceBetween: "small",
    };
  }

  function getCurrencyOptions() {
    const options = [
      {
        ariaLabel: i18n("CurrencySelector__DOLLARS_ARIA_LABEL"),
        displayValue: i18n("CurrencySelector__DOLLARS"),
        label: i18n("CurrencySelector__DOLLARS"),
        value: DOLLARS,
      },
      {
        ariaLabel: i18n("CurrencySelector__POINTS_ARIA_LABEL"),
        displayValue: i18n("CurrencySelector__POINTS"),
        label: i18n("CurrencySelector__POINTS"),
        value: POINTS,
      },
    ];

    if (showDollarsPlusPoints) {
      options.push({
        ariaLabel: i18n("CurrencySelector__DOLLARS_PLUS_POINTS_ARIA_LABEL"),
        displayValue: i18n("CurrencySelector__DOLLARS_PLUS_POINTS"),
        label: i18n("CurrencySelector__DOLLARS_PLUS_POINTS"),
        value: DOLLARS_PLUS_POINTS,
      });
    }

    return options;
  }

  function handleCurrencyButtonClick(selectedIndex) {
    const currencyOptions = getCurrencyOptions();
    const selectedCurrency = currencyOptions[selectedIndex].value;

    onCurrencyChange?.(selectedCurrency);
  }

  function handleCurrencySelectChange(event) {
    const selectedCurrency = event.target.value;

    onCurrencyChange?.(selectedCurrency);
  }
};

CurrencySelector.propTypes = {
  /** Fare type to determine currency option selection. */
  currencyType: PropTypes.oneOf([DOLLARS, POINTS, DOLLARS_PLUS_POINTS]),

  /** Callback function to handle currency selection changes. */
  onCurrencyChange: PropTypes.func,

  /** Whether to show the Dollars + Points option. Defaults to true for backward compatibility. */
  showDollarsPlusPoints: PropTypes.bool,
};
